/*
  AIVIQ homepage sections (aiviq-home-*)
  - Hero video: the poster image shows at once; the video (portrait or 720p on phones, 1080p on
    computers, optionally looping only its opening seconds) starts after the page has loaded.
    Skipped for reduced motion and data saver. A pause button stops it.
  - Rails: arrow buttons scroll one card; they disable at the ends.
  - Bestseller tabs: one product row per tab.
  - Ritual story: the card stays in place while the page scrolls; each scroll step shows the
    next ritual (computer and phone).
  - Compare: the finder questions score the machines and ring the best fit; phones swipe the cards.
  - Capsules: the picked adapters are added to the package and its price.
  - "Læg i kurv": adds via the Cart AJAX API and hands off to the AIVIQ cart drawer.
  Uses event delegation, so it keeps working when sections are re-rendered in the theme editor.
*/
(() => {
  if (window.__aiviqHome) return
  window.__aiviqHome = true

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const saveData = navigator.connection && navigator.connection.saveData
  // a weak connection (Android reports it): 3g or slower, or less than about 5 Mbit/s
  const weakNet = (() => { const c = navigator.connection; return !!c && ((c.effectiveType && c.effectiveType !== '4g') || (c.downlink > 0 && c.downlink < 5)) })()
  const phone = window.matchMedia('(max-width: 767px)')
  const narrow = window.matchMedia('(max-width: 989px)')
  const kr = (cents) => `${new Intl.NumberFormat('da-DK', { maximumFractionDigits: 0 }).format(Math.round(cents / 100))} kr`

  /* ---------- hero video ---------- */
  function startHeroVideos(scope = document) {
    if (reduceMotion || saveData) return
    scope.querySelectorAll('video[data-ah-video]').forEach((video) => {
      if (video.dataset.ahStarted) return
      // computers always get 1080p: 720p stretched across a 1440px screen looked soft
      const src = phone.matches
        ? (weakNet && video.dataset.srcMobileSd) || video.dataset.srcMobile || video.dataset.srcDesktop
        : video.dataset.srcHd || video.dataset.srcDesktop
      if (!src) return
      video.dataset.ahStarted = '1'
      video.src = src
      video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true })
      // Computers can loop just the opening seconds (section setting), so a long film isn't streamed
      const loopEnd = phone.matches ? 0 : Number(video.dataset.loopEnd) || 0
      if (loopEnd > 0) {
        if ('requestVideoFrameCallback' in video) {
          const onFrame = (now, meta) => {
            if (meta.mediaTime >= loopEnd) video.currentTime = 0
            video.requestVideoFrameCallback(onFrame)
          }
          video.requestVideoFrameCallback(onFrame)
        } else {
          video.addEventListener('timeupdate', () => { if (video.currentTime >= loopEnd) video.currentTime = 0 })
        }
      }
      // A first play() can be refused (tab not painted yet, power saving); retry on the next chance
      const tryPlay = () => { if (video.paused && !video.dataset.ahPaused) video.play().catch(() => {}) }
      video.addEventListener('canplay', tryPlay, { once: true })
      document.addEventListener('visibilitychange', () => { if (!document.hidden) tryPlay() })
      ;['pointerdown', 'touchstart', 'scroll', 'keydown'].forEach((ev) =>
        window.addEventListener(ev, tryPlay, { once: true, passive: true })
      )
      tryPlay()
    })
  }
  if (document.readyState === 'complete') setTimeout(startHeroVideos, 200)
  else window.addEventListener('load', () => setTimeout(startHeroVideos, 200), { once: true })

  function togglePause(btn) {
    const hero = btn.closest('.ah-hero')
    const video = hero && hero.querySelector('video[data-ah-video]')
    if (!video) return
    const pause = btn.getAttribute('aria-pressed') !== 'true'
    btn.setAttribute('aria-pressed', pause ? 'true' : 'false')
    btn.setAttribute('aria-label', pause ? btn.dataset.labelPlay : btn.dataset.labelPause)
    if (pause) {
      video.dataset.ahPaused = '1'
      video.pause()
    } else {
      delete video.dataset.ahPaused
      if (!video.dataset.ahStarted) startHeroVideos(hero)
      video.play().catch(() => {})
    }
  }

  /* ---------- rails ---------- */
  function updateArrows(rail) {
    const id = rail.id
    if (!id || rail.hidden) return
    const max = rail.scrollWidth - rail.clientWidth - 2
    document.querySelectorAll(`[data-ah-rail="${id}"]`).forEach((btn) => {
      const dir = Number(btn.dataset.ahDir)
      btn.disabled = dir < 0 ? rail.scrollLeft <= 2 : rail.scrollLeft >= max
    })
  }
  const RAILS = '.ah-rail[id], .ah-cmp__grid[id]'
  function bindRails(scope = document) {
    scope.querySelectorAll(RAILS).forEach((rail) => {
      if (rail.dataset.ahBound) return
      rail.dataset.ahBound = '1'
      rail.addEventListener('scroll', () => updateArrows(rail), { passive: true })
      updateArrows(rail)
    })
  }

  /* ---------- bestseller tabs ---------- */
  function selectTab(tab) {
    const section = tab.closest('[data-ah-tabs]')
    if (!section) return
    const i = tab.dataset.ahTab
    section.querySelectorAll('[data-ah-tab]').forEach((t) => {
      const on = t.dataset.ahTab === i
      t.setAttribute('aria-selected', on ? 'true' : 'false')
      t.tabIndex = on ? 0 : -1
    })
    let active = null
    section.querySelectorAll('[data-ah-panel]').forEach((p) => {
      const on = p.dataset.ahPanel === i
      p.hidden = !on
      if (on) active = p
    })
    // the arrow buttons follow the visible row
    if (active) {
      section.querySelectorAll('.ah-arrow').forEach((a) => { a.dataset.ahRail = active.id })
      active.scrollLeft = 0
      updateArrows(active)
    }
  }

  /* ---------- clicks ---------- */
  document.addEventListener('click', (e) => {
    const arrow = e.target.closest('[data-ah-rail]')
    if (arrow) {
      const rail = document.getElementById(arrow.dataset.ahRail)
      if (!rail) return
      const card = rail.firstElementChild
      const step = card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(rail).columnGap || 16) : rail.clientWidth * 0.8
      rail.scrollBy({ left: step * Number(arrow.dataset.ahDir), behavior: reduceMotion ? 'auto' : 'smooth' })
      return
    }

    const tab = e.target.closest('[data-ah-tab]')
    if (tab) { selectTab(tab); return }

    const add = e.target.closest('[data-ah-add]')
    if (add) {
      e.preventDefault()
      addItems([{ id: Number(add.dataset.ahAdd), quantity: 1 }], add)
      return
    }

    const pause = e.target.closest('[data-ah-pause]')
    if (pause) { togglePause(pause); return }

    const play = e.target.closest('[data-ah-play]')
    if (play) {
      e.preventDefault()
      playReview(play.closest('.ah-vcard'))
      return
    }

    const answer = e.target.closest('[data-ah-answer]')
    if (answer) { pickAnswer(answer); return }

    const reset = e.target.closest('[data-ah-reset]')
    if (reset) { resetFinder(reset.closest('[data-ah-cmp]')); return }

    const stepGo = e.target.closest('[data-ah-step-go]')
    if (stepGo) {
      const cmp = stepGo.closest('[data-ah-cmp]')
      delete cmp.dataset.editing
      goStep(cmp, Number(cmp.dataset.step || 0) + Number(stepGo.dataset.ahStepGo), true)
      return
    }

    const stepTo = e.target.closest('[data-ah-step-to]')
    if (stepTo) {
      const cmp = stepTo.closest('[data-ah-cmp]')
      cmp.dataset.editing = '1'
      goStep(cmp, Number(stepTo.dataset.ahStepTo), true)
      return
    }

    // the spec rows live inside the comparison cards: the grid gets .is-specs
    const specs = e.target.closest('[data-ah-specs-toggle]')
    if (specs) {
      const box = document.getElementById(specs.getAttribute('aria-controls'))
      const open = specs.getAttribute('aria-expanded') !== 'true'
      specs.setAttribute('aria-expanded', open ? 'true' : 'false')
      if (box && box.classList.contains('ah-cmp__grid')) box.classList.toggle('is-specs', open)
      else if (box) box.hidden = !open
      return
    }

    const cap = e.target.closest('[data-ah-capsule]')
    if (cap) { toggleCapsule(cap); return }

    const buy = e.target.closest('[data-ah-buy-package]')
    if (buy) { e.preventDefault(); buyPackage(buy); return }

    const filter = e.target.closest('[data-ah-filter]')
    if (filter) filterReviews(filter)
  })

  // arrow keys move between tabs
  document.addEventListener('keydown', (e) => {
    const tab = e.target.closest && e.target.closest('[data-ah-tab]')
    if (!tab || (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft')) return
    const tabs = [...tab.parentElement.querySelectorAll('[data-ah-tab]')]
    const next = tabs[(tabs.indexOf(tab) + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length]
    next.focus()
    selectTab(next)
  })

  /* ---------- add to cart ---------- */
  async function addItems(items, btn) {
    const label = btn.querySelector('[data-ah-label]')
    const original = label ? label.textContent : ''
    const drawer = document.querySelector('aiviq-cart-drawer')
    btn.classList.add('is-loading')
    if (drawer && typeof drawer.showPending === 'function') drawer.showPending()
    try {
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        // 'cart-ajax' keeps the theme's header cart count in sync
        body: JSON.stringify({ items, sections: 'cart-ajax' })
      })
      const data = await res.json()
      if (!res.ok) throw data
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true, detail: { product: data } }))
      btn.classList.add('is-done')
      if (label) label.textContent = 'Lagt i kurven'
      setTimeout(() => {
        btn.classList.remove('is-done')
        if (label) label.textContent = original
      }, 2500)
      // No drawer (e.g. on the cart page): fall back to the cart page
      if (!drawer) window.location.href = `${root}cart`
    } catch (err) {
      document.dispatchEvent(new CustomEvent('ajaxProduct:error', { bubbles: true, detail: err }))
      if (label) label.textContent = (err && err.description) || 'Kunne ikke tilføjes'
      setTimeout(() => {
        if (label) label.textContent = original
      }, 3000)
    } finally {
      btn.classList.remove('is-loading')
    }
  }

  /* ---------- review videos ---------- */
  function playReview(card) {
    if (!card || card.classList.contains('is-playing')) return
    const src = card.dataset.src
    if (!src) return
    const video = document.createElement('video')
    video.src = src
    video.controls = true
    video.playsInline = true
    video.preload = 'auto'
    card.append(video)
    card.classList.add('is-playing')
    video.play().catch(() => {})
  }

  /* ---------- review filters ---------- */
  function filterReviews(btn) {
    const section = btn.closest('[data-ah-reviews]')
    if (!section) return
    const cat = btn.dataset.ahFilter
    section.querySelectorAll('[data-ah-filter]').forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'))
    section.querySelectorAll('[data-ah-cat]').forEach((card) => {
      card.hidden = cat !== 'all' && card.dataset.ahCat !== cat
    })
    const rail = section.querySelector('.ah-rail')
    if (rail) { rail.scrollLeft = 0; updateArrows(rail) }
  }

  /* ---------- ritual story (aiviq-home-feature) ---------- */
  // The first sticky element at the top of the page (the theme header) decides where the card pins
  // Expanse keeps the header in a 0px-tall sticky wrapper (#StickyHeaderWrapper) and positions the
  // visible bar absolutely inside it, so measure header elements that sit in a sticky/fixed ancestor
  function sticks(el) {
    for (let p = el; p && p !== document.body; p = p.parentElement) {
      const pos = getComputedStyle(p).position
      if (pos === 'sticky' || pos === 'fixed') return true
    }
    return false
  }

  function headerOffset() {
    let bottom = 0
    document.querySelectorAll('.shopify-section-group-header-group, header, [data-sticky-header], .site-header, .header-wrapper').forEach((el) => {
      if (!sticks(el)) return
      const r = el.getBoundingClientRect()
      if (r.top <= 1 && r.bottom > bottom && r.bottom < window.innerHeight / 2) bottom = r.bottom
    })
    return Math.round(bottom)
  }

  function initFeatureSlider(el) {
    if (el.dataset.ahBound) return
    el.dataset.ahBound = '1'
    const slides = [...el.querySelectorAll('[data-ah-fs-slide]')]
    const dots = [...el.querySelectorAll('[data-ah-fs-dot]')]
    const count = el.querySelector('[data-ah-fs-count]')
    const track = el.querySelector('.ah-fs__track')
    const stage = el.querySelector('.ah-fs__stage')
    const n = slides.length
    let index = 0
    let step = 0
    let pinTop = 0

    function go(i) {
      i = Math.max(0, Math.min(n - 1, i))
      if (i === index) return
      index = i
      slides.forEach((s, k) => {
        const on = k === i
        s.classList.toggle('is-active', on)
        s.toggleAttribute('inert', !on)
        s.setAttribute('aria-hidden', on ? 'false' : 'true')
      })
      dots.forEach((d, k) => {
        d.classList.toggle('is-active', k === i)
        if (k === i) d.setAttribute('aria-current', 'true')
        else d.removeAttribute('aria-current')
      })
      if (count) count.textContent = String(i + 1).padStart(2, '0')
      el.classList.toggle('is-last', i === n - 1)
    }

    function layout() {
      // full width on every screen now, so it pins right under the header
      const top = headerOffset()
      el.style.setProperty('--ah-fs-top', `${top}px`)
      if (n < 2) { track.style.height = ''; return }
      // Based on the card height (svh on phones), not innerHeight, which changes as the phone's toolbar moves
      step = Math.round(stage.offsetHeight * (narrow.matches ? 0.7 : 0.55))
      pinTop = top
      track.style.height = `${stage.offsetHeight + (n - 1) * step + Math.round(step * 0.3)}px`
      onScroll()
    }
    function onScroll() {
      if (n < 2 || !step) return
      const scrolled = pinTop - track.getBoundingClientRect().top
      go(Math.floor((scrolled + step * 0.5) / step))
    }

    dots.forEach((d) =>
      d.addEventListener('click', () => {
        const i = Number(d.dataset.ahFsDot)
        const y = window.scrollY + track.getBoundingClientRect().top - pinTop + i * step
        window.scrollTo({ top: y, behavior: reduceMotion ? 'auto' : 'smooth' })
      })
    )

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', layout, { passive: true })
    narrow.addEventListener('change', layout)
    layout()
    // the header can change height once its own scripts run
    setTimeout(layout, 600)
  }

  /* ---------- compare (aiviq-home-compare) ----------
     Each card says what the machine has (data-milk, data-control, data-filter, data-price). Every
     answer gives points to the machines that suit it; the budget answer leaves out machines above
     it. Highest score wins; on a tie the cheaper machine wins, or with a budget the one that gives
     most for the money. */
  const FIT = {
    milk: {
      rarely: (m) => (m.milk === 'wand' ? 1 : 0),
      often: (m) => (m.milk === 'dual' || m.milk === 'auto' ? 2 : 0),
      auto: (m) => (m.milk === 'auto' ? 4 : m.milk === 'dual' ? 1 : 0),
    },
    control: {
      simple: (m) => (m.control === 'knobs' ? 2 : 0),
      display: (m) => (m.control === 'display' ? 3 : m.control === 'touch' ? 1 : 0),
      full: (m) => (m.control === 'touch' ? 4 : m.control === 'display' ? 1 : 0),
    },
    drinks: {
      espresso: () => 0,
      filter: (m) => (m.filter ? 4 : 0),
    },
  }

  function finderAnswers(cmp) {
    const out = {}
    cmp.querySelectorAll('[data-ah-answer][aria-pressed="true"]').forEach((b) => {
      out[b.dataset.ahAnswer] = b.dataset.value
    })
    return out
  }

  function bestMachine(cards, answers, withBudget) {
    const cap = withBudget && answers.budget && answers.budget !== 'any' ? Number(answers.budget) : Infinity
    let best = null
    cards.forEach((card) => {
      const m = {
        milk: card.dataset.milk,
        control: card.dataset.control,
        filter: card.dataset.filter === 'true',
        price: Number(card.dataset.price) || 0,
      }
      if (m.price > cap) return
      let score = 0
      Object.keys(FIT).forEach((q) => {
        const fit = answers[q] && FIT[q][answers[q]]
        if (fit) score += fit(m)
      })
      const tieWin = cap !== Infinity ? m.price > best?.price : m.price < best?.price
      if (!best || score > best.score || (score === best.score && tieWin)) best = { card, score, price: m.price }
    })
    return best
  }

  function pickAnswer(btn) {
    const cmp = btn.closest('[data-ah-cmp]')
    if (!cmp) return
    const q = btn.dataset.ahAnswer
    // a second tap on the chosen answer clears the question
    const on = btn.getAttribute('aria-pressed') !== 'true'
    cmp.querySelectorAll(`[data-ah-answer="${q}"]`).forEach((b) => b.setAttribute('aria-pressed', on && b === btn ? 'true' : 'false'))
    runFinder(cmp)
    if (!on) return
    // an answer moves on to the next question (or back to the answers, when one was being changed)
    const steps = finderSteps(cmp)
    const i = steps.indexOf(btn.closest('[data-ah-step]'))
    if (i < 0) return
    const next = cmp.dataset.editing ? steps.length : i + 1
    delete cmp.dataset.editing
    setTimeout(() => goStep(cmp, next, true), 260)
  }

  function resetFinder(cmp) {
    cmp.querySelectorAll('[data-ah-answer]').forEach((b) => b.setAttribute('aria-pressed', 'false'))
    delete cmp.dataset.editing
    runFinder(cmp)
    goStep(cmp, 0, false)
  }

  /* one question at a time; after the last one the answers are listed and can be tapped to change */
  const PENCIL = '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><path d="M8 1.5 10.5 4 4 10.5H1.5V8z"/></svg>'

  function finderSteps(cmp) {
    return [...cmp.querySelectorAll('[data-ah-step]')]
  }

  function goStep(cmp, i, focus) {
    const steps = finderSteps(cmp)
    const n = steps.length
    if (!n) return
    i = Math.max(0, Math.min(n, i))
    cmp.dataset.step = String(i)
    const done = i >= n
    steps.forEach((s, k) => { s.hidden = k !== i })
    const label = cmp.querySelector('[data-ah-step-label]')
    if (label) label.textContent = done ? label.dataset.done : (label.dataset.template || '').replace('[nr]', i + 1).replace('[antal]', n)
    cmp.querySelectorAll('.ah-cmp__progress i').forEach((bar, k) => bar.classList.toggle('is-on', done || k <= i))
    const back = cmp.querySelector('[data-ah-step-go="-1"]')
    const skip = cmp.querySelector('[data-ah-step-go="1"]')
    if (back) back.hidden = i === 0
    if (skip) skip.hidden = done
    const summary = cmp.querySelector('[data-ah-summary]')
    if (summary) {
      summary.hidden = !done
      if (done) fillSummary(summary, steps)
    }
    if (!focus) return
    const target = done ? summary && summary.querySelector('button') : steps[i].querySelector('[data-ah-answer]')
    if (target) target.focus({ preventScroll: true })
  }

  function fillSummary(summary, steps) {
    summary.textContent = ''
    steps.forEach((s, k) => {
      const on = s.querySelector('[data-ah-answer][aria-pressed="true"]')
      if (!on) return
      const b = document.createElement('button')
      b.type = 'button'
      b.className = 'ah-chip ah-chip--sm'
      b.dataset.ahStepTo = String(k)
      b.textContent = on.textContent.trim()
      b.insertAdjacentHTML('beforeend', PENCIL)
      const question = s.querySelector('.ah-cmp__qlabel')
      b.setAttribute('aria-label', `${summary.dataset.editLabel || ''} ${question ? question.textContent.trim() : ''} ${on.textContent.trim()}`.trim())
      summary.append(b)
    })
  }

  function runFinder(cmp) {
    const cards = [...cmp.querySelectorAll('.ah-cmp__card')]
    const answers = finderAnswers(cmp)
    const any = Object.keys(answers).length > 0
    const pick = any ? bestMachine(cards, answers, true) : null
    cards.forEach((c) => c.classList.toggle('is-match', !!pick && c === pick.card))

    const result = cmp.querySelector('[data-ah-result]')
    const hint = cmp.querySelector('[data-ah-hint]')
    const reset = cmp.querySelector('[data-ah-reset]')
    if (reset) reset.hidden = !any
    if (hint) hint.hidden = true
    if (!result) return
    if (!pick) {
      result.textContent = result.dataset.empty
      return
    }
    const d = pick.card.dataset
    result.textContent = `${result.dataset.prefix} `
    const name = document.createElement('b')
    name.textContent = d.name
    result.append(name)
    if (d.best) {
      const why = document.createElement('span')
      why.className = 'ah-cmp__why'
      why.textContent = d.best
      result.append(why)
    }
    // a machine above the budget that fits the answers better gets a one-line mention
    if (hint && answers.budget && answers.budget !== 'any') {
      const free = bestMachine(cards, answers, false)
      if (free && free.card !== pick.card && free.score > pick.score) {
        hint.textContent = (hint.dataset.template || '').replace('[navn]', free.card.dataset.name).replace('[pris]', free.card.dataset.priceLabel)
        hint.hidden = !hint.textContent
      }
    }
    showCard(cmp, pick.card)
  }

  // phones: swipe the match into view, next to the machine before it
  function showCard(cmp, card) {
    const grid = cmp.querySelector('.ah-cmp__grid')
    if (!grid || grid.scrollWidth <= grid.clientWidth + 2) return
    const cards = [...grid.querySelectorAll('.ah-cmp__card')]
    const target = cards[Math.max(0, cards.indexOf(card) - 1)]
    const pad = parseFloat(getComputedStyle(grid).paddingLeft) || 0
    const left = target.getBoundingClientRect().left - grid.getBoundingClientRect().left + grid.scrollLeft - pad
    grid.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' })
  }

  /* ---------- capsules (aiviq-home-capsules) ---------- */
  // the package price is today's total; items on offer add their compare-at price to the struck line
  function updatePackage(box) {
    const machine = box.querySelector('[data-ah-machine]')
    if (!machine) return
    const was = (el) => Math.max(Number(el.dataset.compare) || 0, Number(el.dataset.price))
    let total = Number(machine.dataset.price)
    let before = was(machine)
    const names = [machine.dataset.name]
    box.querySelectorAll('[data-ah-capsule][aria-pressed="true"]').forEach((c) => {
      if (c.dataset.included === 'true') return
      total += Number(c.dataset.price)
      before += was(c)
      names.push(c.dataset.name)
    })
    const pkg = box.querySelector('[data-ah-pkg-name]')
    const sum = box.querySelector('[data-ah-pkg-total]')
    if (pkg) pkg.textContent = names.join(' + ')
    if (!sum) return
    const onSale = before > total
    sum.classList.toggle('is-sale', onSale)
    sum.querySelector('[data-ah-pkg-now]').textContent = kr(total)
    sum.querySelector('[data-ah-pkg-was-v]').textContent = kr(before)
    sum.querySelector('[data-ah-pkg-was]').hidden = !onSale
  }
  function toggleCapsule(chip) {
    if (chip.dataset.included === 'true') return
    chip.setAttribute('aria-pressed', chip.getAttribute('aria-pressed') === 'true' ? 'false' : 'true')
    updatePackage(chip.closest('[data-ah-caps]'))
  }
  function buyPackage(btn) {
    const box = btn.closest('[data-ah-caps]')
    const machine = box.querySelector('[data-ah-machine]')
    const items = [{ id: Number(machine.dataset.variant), quantity: 1 }]
    box.querySelectorAll('[data-ah-capsule][aria-pressed="true"]').forEach((c) => {
      if (c.dataset.included !== 'true' && c.dataset.variant) items.push({ id: Number(c.dataset.variant), quantity: 1 })
    })
    addItems(items, btn)
  }

  /* ---------- init + theme editor ---------- */
  // Anchor jumps (#find-din-maskine) land below the sticky header: .ah[id] uses this in scroll-margin-top
  function setHeaderVar() {
    const h = headerOffset()
    if (h) document.documentElement.style.setProperty('--ah-header-h', `${h}px`)
  }

  function init(scope) {
    setHeaderVar()
    scope.querySelectorAll('[data-ah-fs]').forEach(initFeatureSlider)
    scope.querySelectorAll('[data-ah-caps]').forEach(updatePackage)
    bindRails(scope)
    if (document.readyState === 'complete') startHeroVideos(scope)
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document))
  else init(document)
  window.addEventListener('resize', () => {
    document.querySelectorAll(RAILS).forEach(updateArrows)
    setHeaderVar()
  }, { passive: true })
  document.addEventListener('shopify:section:load', (e) => init(e.target))
})()
